import React, { useMemo, useState } from "react";
import { Input, Label, Button } from "reactstrap";
import { getI18n, withI18n } from "react-i18next";
import { Link as ReactRouterLink, withRouter } from "react-router-dom";
import styled, { css } from "styled-components";
import AcceptTerms from "../../components/AcceptTerms";
import ErrorMessage from "../../components/ErrorMessage";
import * as SharedStyle from "common/SharedStyledComponents";
import TextInput from "common/TextInput";
import { getRegisterRedirect } from "../../../../shared/helpers";
import EyeClosedIcon from "../../../../images/eye-closed.svg";
import EyeOpenIcon from "../../../../images/eye-open.svg";

const CWBLogin = ({
  t,
  location,
  checkedTerms,
  errors,
  errorMessage,
  isMissingAcceptance,
  rememberMe,
  showFakePassword,
  user,
  onChangeField,
  onCheckTerms,
  onKeyPress,
  onSubmit,
  toggleRememberMe,
}) => {
  const isLangSpanish = getI18n().language === "es";
  const terms = isLangSpanish
    ? CWB_API_ENV.termsEs
    : `${CWB_API_ENV.domain}/terms_conditions.asp`;
  const privacy = isLangSpanish
    ? CWB_API_ENV.privacyEs
    : `${CWB_API_ENV.domain}/privacy.asp`;

  const signupRedirect = useMemo(() => {
    return getRegisterRedirect(location);
  }, [location]);

  const [isPassHidden, setPassHidden] = useState(true);

  return (
    <Base>
      <Wrapper>
        <SharedStyle.NotifWrapper>
          <NotifText>{t("Say hello to 'theWorkbook'")}</NotifText>
          <NotifLink as='a' href={t("theWorkbookRelease")} secondary>{t("See why we made this change")}</NotifLink>
        </SharedStyle.NotifWrapper>
      </Wrapper>
      <Wrapper>
        <SharedStyle.FormWrapper>
          <SharedStyle.FormHeader>{t("Login")}</SharedStyle.FormHeader>
          <LoginForm>
            <TextInput
              error={Boolean(errors.workbookId)}
              errorMessage={errors.workbookId}
              label={t("Username or Workbook ID")}
              placeholder={t("Enter Details")}
              value={user.workbookId}
              onChange={(e) => onChangeField("workbookId", e.target.value)}
              onKeyPress={onKeyPress}
            />
            <PassWrapper>
              <StyledTextInput
                error={Boolean(errors.password)}
                errorMessage={errors.password}
                label={t("Password")}
                placeholder={t("Enter Details")}
                type={isPassHidden ? "password" : "text"}
                value={showFakePassword ? "XXXXXXX" : user.password}
                onChange={(e) => onChangeField("password", e.target.value)}
                onKeyPress={onKeyPress}
              />
              {showFakePassword
                ? null
                : <PassHideButton title={t("Show password")} onClick={() => setPassHidden(isPassHidden => !isPassHidden)}>
                  {isPassHidden
                    ? <PassHideImg src={EyeClosedIcon} />
                    : <PassHideImg src={EyeOpenIcon} />
                  }
                </PassHideButton>
              }
            </PassWrapper>
            <div style={{ marginTop: "6px" }}>
              <StyledCheckbox
                id="remember-me"
                type="checkbox"
                checked={rememberMe === true}
                onChange={toggleRememberMe}
              />
              <RememberMeText for="remember-me">
                {t("Remember Me")}
              </RememberMeText>
            </div>
            {!isMissingAcceptance && (
              <ErrorMessage error={errorMessage}></ErrorMessage>
            )}
            {isMissingAcceptance && (
              <AcceptTerms isSelected={checkedTerms} onChange={onCheckTerms} />
            )}
            <SharedStyle.SubmitButton
              id="login-btn"
              className="cwb-btn-primary"
              onClick={onSubmit}
            >
              {t("Login")}
            </SharedStyle.SubmitButton>
            <StyledSection>
              {isLangSpanish ?
                (
                  <StyledDiv>
                    <StyledSpan>
                      ¿No tienes cuenta con Casting Workbook?
                    </StyledSpan>
                    &nbsp;
                    <StyledLink href={signupRedirect}>
                      Primero crea una gratis,
                    </StyledLink>
                    <NormalSpan>luego puedes mejorar tu membresía cuando seas redireccionado a la bandeja de entrada.</NormalSpan>
                  </StyledDiv>
                ) :
                (<StyledDiv>
                  <StyledSpan>
                    {t("Don't have a Casting Workbook account?")}
                  </StyledSpan>
                  &nbsp;
                  <StyledLink href={signupRedirect}>
                    {t("First create one for free")}
                  </StyledLink>,
                  <br />
                  <StyledSpan>
                    {t("then you can upgrade after being re-directed to the homepage.")}
                  </StyledSpan>
                </StyledDiv>
                )}
              <div>
                <StyledReactRouterLink
                  to={`/login/forgotPwd${location.search}`}
                >
                  {t("I forgot my password")}
                </StyledReactRouterLink>
              </div>
            </StyledSection>
          </LoginForm>
          <Legal>
            <LegalLink href={terms} rel="noopener noreferrer" target="_blank">
              {t("Terms and Conditions")}
            </LegalLink>
            <Divider />
            <LegalLink href={privacy} rel="noopener noreferrer" target="_blank">
              {t("Privacy Policy")}
            </LegalLink>
          </Legal>
        </SharedStyle.FormWrapper>
      </Wrapper>
    </Base>
  );
};

const NotifText = styled.p`
  color: ${p => p.theme.color};
  margin-top: 0;
  font-weight: 900;
  margin-bottom: 0;

  font-size: 16px;
`;

const NotifLink = styled.a`
  color: ${p => p.theme.color};
  margin-top: 0;
  margin-bottom: 0;

  font-size: 12px;
  font-weight: 700;
  text-decoration: underline;

  color: #0f9bf0;
  &:hover {
    color: #0892e5;
  }

  &:active {
    color: #0584d7;
  }

`;

const NormalSpan = styled.span`
  font-style: normal !important;
  text-decoration: none !important;
  font-size: 14px;
`
const Base = styled.div`
  width: 100%;
  height: calc(100vh - 60px);
  padding: 10vh 0;

  @media (max-width: 375px) {
    padding: 5vh 0;
  }
`;

const Wrapper = styled.div`
  width: 610px;
  margin: auto;
  background: white;

  @media (max-width: 475px) {
    width: 100%;
  }
`;

const LoginForm = styled.div`
  padding: 24px 50px 16px;
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (max-width: 475px) {
    padding-left: 15px;
    padding-right: 15px;
  }
`;

const StyledTextInput = styled(TextInput)`
  margin-top: 16px;
`;

const RememberMeText = styled(Label)`
  display: inline;
  font-size: 12px;
`;

const StyledCheckbox = styled(Input)`
  position: relative;
  margin-left: 0px;
  margin-right: 8px;
`;

const StyledSection = styled.section`
  min-height: 64px;
  display: flex;
  flex-flow: column nowrap;
  justify-content: space-between;
  margin: 16px 0;

  & > div {
    text-align: center;
  }
`;

const StyledDiv = styled.div`
  display: flex;
  flex-flow: row wrap;
  justify-content: center;
  margin-bottom: 8px;
`;

const StyledSpan = styled.span`
  color: #0a1f44;
  font-size: 14px;
  font-weight: 700;
`;

const StyledLink = styled.a`
  color: #0f9bf0;
  font-size: 14px;
  font-weight: 700;
  text-decoration: underline;

  &:hover {
    color: #0892e5;
  }

  &:active {
    color: #0584d7;
  }
`;

const StyledReactRouterLink = StyledLink.withComponent(ReactRouterLink);

const LegalLink = styled.a`
  color: #53627c;
  font-size: 12px;
  font-weight: 400;
  line-height: 21px;
  text-decoration: none;
  display: block;

  &:hover {
    color: #53627c;
    text-decoration: underline;
  }
`;

const Legal = styled.div`
  width: 100%;
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  color: grey;
  padding: 16px 0;
  border-top: 1px solid #f1f2f4;
  text-align: center;
`;

const Divider = styled.div`
  width: 1px;
  height: 22px;
  margin: 0 24px;
  background: #8a94a6;
`;

const PassWrapper = styled.div`
  position: relative;
`;

const PassHideButton = styled.button`
  position: absolute;
  border: none;
  background-color: transparent;
  right: 4px;
  top: 48px;
`;

const PassHideImg = styled.img`

`;

export default withRouter(withI18n()(CWBLogin));
